'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import type { GeometryPayload } from './types';
import { loadJSXGraph } from '@/lib/loadJSXGraph';

const GEOMETRY_DEFAULT_COLORS = {
  point: '#2f2a26',
  segment: '#1f5fbf',
  ray: '#1d4ed8',
  line: '#b45309',
  circle: '#0f766e',
  angle: '#c2410c',
  polygonStroke: '#1f2937',
  polygonFill: '#fde68a',
  label: '#3f3f46',
  text: '#374151',
  axis: '#4b5563',
  grid: '#d1d5db',
  tick: '#6b7280',
};

interface GeometryEntryProps {
  payload: GeometryPayload;
}

function directedAngleDegrees(
  p1: [number, number],
  vertex: [number, number],
  p2: [number, number],
) {
  const a1 = Math.atan2(p1[1] - vertex[1], p1[0] - vertex[0]);
  const a2 = Math.atan2(p2[1] - vertex[1], p2[0] - vertex[0]);
  let diff = ((a2 - a1) * 180) / Math.PI;
  while (diff < 0) diff += 360;
  while (diff >= 360) diff -= 360;
  return diff;
}

function sanitizePayload(payload: GeometryPayload): Required<GeometryPayload> {
  const xMin = Number.isFinite(payload.xMin) ? payload.xMin : -10;
  const xMaxRaw = Number.isFinite(payload.xMax) ? payload.xMax : 10;
  const yMin = Number.isFinite(payload.yMin) ? payload.yMin : -10;
  const yMaxRaw = Number.isFinite(payload.yMax) ? payload.yMax : 10;

  return {
    xMin,
    xMax: xMaxRaw > xMin ? xMaxRaw : xMin + 1,
    yMin,
    yMax: yMaxRaw > yMin ? yMaxRaw : yMin + 1,
    aspectRatio: payload.aspectRatio === '3 / 4' || payload.aspectRatio === '16 / 9' || payload.aspectRatio === '1 / 1'
      ? payload.aspectRatio
      : '4 / 3',
    xStep: payload.xStep && payload.xStep > 0 ? payload.xStep : 1,
    yStep: payload.yStep && payload.yStep > 0 ? payload.yStep : 1,
    showAxis: payload.showAxis ?? false,
    showGrid: payload.showGrid ?? false,
    points: payload.points ?? [],
    segments: payload.segments ?? [],
    rays: payload.rays ?? [],
    lines: payload.lines ?? [],
    circles: payload.circles ?? [],
    angles: payload.angles ?? [],
    polygons: payload.polygons ?? [],
    texts: payload.texts ?? [],
  };
}

function collectBounds(data: Required<GeometryPayload>) {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const consider = (x: number, y: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  };

  data.points.forEach((point) => consider(point.x, point.y));
  data.segments.forEach((segment) => {
    consider(segment.from[0], segment.from[1]);
    consider(segment.to[0], segment.to[1]);
  });
  data.rays.forEach((ray) => {
    consider(ray.from[0], ray.from[1]);
    consider(ray.through[0], ray.through[1]);
  });
  data.lines.forEach((line) => {
    consider(line.p1[0], line.p1[1]);
    consider(line.p2[0], line.p2[1]);
  });
  data.circles.forEach((circle) => {
    consider(circle.center[0], circle.center[1]);
    if (Number.isFinite(circle.radius)) {
      const r = Number(circle.radius);
      consider(circle.center[0] - r, circle.center[1] - r);
      consider(circle.center[0] + r, circle.center[1] + r);
    } else if (Array.isArray(circle.through) && circle.through.length === 2) {
      consider(circle.through[0], circle.through[1]);
    }
  });
  data.angles.forEach((angle) => {
    consider(angle.vertex[0], angle.vertex[1]);
    consider(angle.p1[0], angle.p1[1]);
    consider(angle.p2[0], angle.p2[1]);
  });
  data.polygons.forEach((polygon) => {
    polygon.vertices.forEach((vertex) => consider(vertex.x, vertex.y));
  });
  data.texts.forEach((item) => consider(item.x, item.y));

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }

  return { minX, maxX, minY, maxY };
}

function roundDownToStep(value: number, step: number) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value;
  return Math.floor(value / step) * step;
}

function roundUpToStep(value: number, step: number) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value;
  return Math.ceil(value / step) * step;
}

function computeAutoBounds(data: Required<GeometryPayload>) {
  const bounds = collectBounds(data);
  if (!bounds) return null;

  const spanX = Math.max(bounds.maxX - bounds.minX, 0);
  const spanY = Math.max(bounds.maxY - bounds.minY, 0);
  const paddingX = Math.max(spanX * 0.2, 1);
  const paddingY = Math.max(spanY * 0.2, 1);
  const minSpan = 4;

  const targetSpanX = Math.max(spanX + paddingX * 2, minSpan);
  const targetSpanY = Math.max(spanY + paddingY * 2, minSpan);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  const rawXMin = centerX - targetSpanX / 2;
  const rawXMax = centerX + targetSpanX / 2;
  const rawYMin = centerY - targetSpanY / 2;
  const rawYMax = centerY + targetSpanY / 2;

  const stepX = data.xStep || 1;
  const stepY = data.yStep || 1;

  return {
    xMin: roundDownToStep(rawXMin, stepX),
    xMax: roundUpToStep(rawXMax, stepX),
    yMin: roundDownToStep(rawYMin, stepY),
    yMax: roundUpToStep(rawYMax, stepY),
  };
}

export function GeometryEntry({ payload }: GeometryEntryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<any>(null);
  const boardId = useMemo(() => `jxg-geometry-${Math.random().toString(36).slice(2)}`, []);
  const aspectRatio = payload.aspectRatio === '3 / 4' || payload.aspectRatio === '16 / 9' || payload.aspectRatio === '1 / 1'
    ? payload.aspectRatio
    : '4 / 3';

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      if (!containerRef.current) return;

      const data = sanitizePayload(payload);
      const autoBounds = computeAutoBounds(data);
      const useAutoBounds = autoBounds
        && (
          (autoBounds.xMax - autoBounds.xMin) < (data.xMax - data.xMin) * 0.9
          || (autoBounds.yMax - autoBounds.yMin) < (data.yMax - data.yMin) * 0.9
        );
      const xMin = useAutoBounds ? autoBounds!.xMin : data.xMin;
      const xMax = useAutoBounds ? autoBounds!.xMax : data.xMax;
      const yMin = useAutoBounds ? autoBounds!.yMin : data.yMin;
      const yMax = useAutoBounds ? autoBounds!.yMax : data.yMax;
      const JXG = await loadJSXGraph();
      if (!isMounted || !containerRef.current) return;

      if (boardRef.current) {
        JXG.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }

      const board = JXG.JSXGraph.initBoard(boardId, {
        boundingbox: [xMin, yMax, xMax, yMin],
        keepaspectratio: true,
        showNavigation: false,
        showCopyright: false,
        pan: { enabled: false },
        zoom: { enabled: false },
        axis: data.showAxis,
        grid: data.showGrid,
        lineColor: GEOMETRY_DEFAULT_COLORS.axis,
        axisColor: GEOMETRY_DEFAULT_COLORS.axis,
        gridColor: GEOMETRY_DEFAULT_COLORS.grid,
        tickColor: GEOMETRY_DEFAULT_COLORS.tick,
        label: { color: GEOMETRY_DEFAULT_COLORS.label },
      });
      boardRef.current = board;

      data.points.forEach((point) => {
        board.create('point', [point.x, point.y], {
          name: point.label || '',
          size: 4,
          face: 'o',
          fillColor: point.style === 'hollow' ? '#ffffff' : (point.color || GEOMETRY_DEFAULT_COLORS.point),
          strokeColor: point.color || GEOMETRY_DEFAULT_COLORS.point,
          fixed: true,
          highlight: false,
          label: { color: GEOMETRY_DEFAULT_COLORS.label },
        });
      });

      data.segments.forEach((segment) => {
        const p1 = board.create('point', segment.from, { visible: false, fixed: true, withLabel: false, name: '' });
        const p2 = board.create('point', segment.to, { visible: false, fixed: true, withLabel: false, name: '' });
        board.create('segment', [p1, p2], {
          strokeColor: segment.color || GEOMETRY_DEFAULT_COLORS.segment,
          strokeWidth: 2.5,
          fixed: true,
          highlight: false,
          dash: segment.dashed ? 2 : 0,
        });
      });

      data.rays.forEach((ray) => {
        const p1 = board.create('point', ray.from, { visible: false, fixed: true, withLabel: false, name: '' });
        const p2 = board.create('point', ray.through, { visible: false, fixed: true, withLabel: false, name: '' });
        board.create('line', [p1, p2], {
          straightFirst: false,
          straightLast: true,
          strokeColor: ray.color || GEOMETRY_DEFAULT_COLORS.ray,
          strokeWidth: 2.3,
          fixed: true,
          highlight: false,
          dash: ray.dashed ? 2 : 0,
        });
      });

      data.lines.forEach((line) => {
        const p1 = board.create('point', line.p1, { visible: false, fixed: true, withLabel: false, name: '' });
        const p2 = board.create('point', line.p2, { visible: false, fixed: true, withLabel: false, name: '' });
        board.create('line', [p1, p2], {
          strokeColor: line.color || GEOMETRY_DEFAULT_COLORS.line,
          strokeWidth: 2.3,
          fixed: true,
          highlight: false,
          dash: line.dashed ? 2 : 0,
        });
      });

      data.circles.forEach((circle) => {
        const center = board.create('point', circle.center, { visible: false, fixed: true, withLabel: false, name: '' });
        if (Number.isFinite(circle.radius)) {
          board.create('circle', [center, Number(circle.radius)], {
            strokeColor: circle.color || GEOMETRY_DEFAULT_COLORS.circle,
            strokeWidth: 2.2,
            fixed: true,
            highlight: false,
          });
        } else if (Array.isArray(circle.through) && circle.through.length === 2) {
          const through = board.create('point', circle.through, { visible: false, fixed: true, withLabel: false, name: '' });
          board.create('circle', [center, through], {
            strokeColor: circle.color || GEOMETRY_DEFAULT_COLORS.circle,
            strokeWidth: 2.2,
            fixed: true,
            highlight: false,
          });
        }
      });

      data.angles.forEach((angle) => {
        // Prefer the acute/obtuse interior angle (<= 180deg) instead of reflex exterior angle.
        const useSwappedOrder = directedAngleDegrees(angle.p1, angle.vertex, angle.p2) > 180;
        const firstArm = useSwappedOrder ? angle.p2 : angle.p1;
        const secondArm = useSwappedOrder ? angle.p1 : angle.p2;
        const p1 = board.create('point', firstArm, { visible: false, fixed: true, withLabel: false, name: '' });
        const vertex = board.create('point', angle.vertex, { visible: false, fixed: true, withLabel: false, name: '' });
        const p2 = board.create('point', secondArm, { visible: false, fixed: true, withLabel: false, name: '' });
        board.create('angle', [p1, vertex, p2], {
          radius: 0.8,
          strokeColor: angle.color || GEOMETRY_DEFAULT_COLORS.angle,
          fillColor: angle.color || GEOMETRY_DEFAULT_COLORS.angle,
          fillOpacity: 0.15,
          fixed: true,
          highlight: false,
          name: angle.label || '',
          label: { color: GEOMETRY_DEFAULT_COLORS.label },
        });
      });

      data.polygons.forEach((polygon) => {
        const vertices = polygon.vertices.map((vertex) => board.create('point', [vertex.x, vertex.y], {
          visible: false,
          fixed: true,
          withLabel: false,
          name: '',
        }));
        board.create('polygon', vertices, {
          borders: {
            strokeColor: polygon.color || GEOMETRY_DEFAULT_COLORS.polygonStroke,
            strokeWidth: 2,
            fixed: true,
            highlight: false,
          },
          fillColor: polygon.fillColor || GEOMETRY_DEFAULT_COLORS.polygonFill,
          fillOpacity: Number.isFinite(polygon.opacity) ? polygon.opacity : 0.18,
          fixed: true,
          highlight: false,
        });
      });

      data.texts.forEach((item) => {
        if (!item.text || !item.text.trim()) return;
        board.create('text', [item.x, item.y, item.text], {
          fixed: true,
          highlight: false,
          color: item.color || GEOMETRY_DEFAULT_COLORS.text,
          fontSize: Number.isFinite(item.fontSize) ? Number(item.fontSize) : 13,
        });
      });

      board.update();
    };

    void init();

    return () => {
      isMounted = false;
      if (boardRef.current && window.JXG?.JSXGraph) {
        window.JXG.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }
    };
  }, [boardId, payload]);

  return (
    <div
      id={boardId}
      ref={containerRef}
      style={{ width: '100%', maxWidth: 760, aspectRatio, margin: '0 auto', backgroundColor: '#ffffff' }}
    />
  );
}
